import { useFn } from '@eturi/react'
import { microtask } from '@eturi/util'
import type { ReactNode } from 'react'
import { useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { v4 } from 'uuid'
import { BackHistoryContext } from './BackHistoryContext.js'

/**
 * Provider to manage history back button actions. This exists to centralize browser history event
 * navigation handlers, which are required to solve a few problems with managing history events.
 *
 * Fundamentally, this is used in modals or overlays (carousel) to allow the user to close the view
 * using the browser back button. The basic implementation concept is simple: When a component
 * mounts, it pushes a duplicate history record and listens to history events. When a 'POP' event
 * occurs, we call the appropriate handler, allowing the overlay to close.
 *
 * There are several problems associated with using history like this, and this implementation
 * solves the main problems:
 *
 * 1. Allowing modals to stack: We don't currently use this functionality, but it works due to how
 *    we have to handle modal transitions, where one modal closes and another opens in the same
 *    render cycle.
 *
 * 2. Properly cleaning up history records: Since overlays can be closed in multiple ways, we need
 *    to track whether the user performs history events that clean the extra history record. If
 *    they don't perform these events, we need to clean the history records ourselves, otherwise
 *    the history becomes increasingly polluted with extra records, and navigation does not work
 *    the way it should. This issue involves both closing modals programmatically (via UI close
 *    button or other state logic), but also involves clean up required when the user navigates
 *    to a new route when the overlay is open.
 *
 * 3. Allowing overlays to open and close in the same render cycle: If one modal opens in the same
 *    render cycle as another closes, we run into an issue. If the logic runs independently in each
 *    component, the closing modal will clean up by calling `goBack()`, and the opening modal will
 *    register its history listener. The `goBack` from the closing modal will asynchronously call
 *    a 'POP', and this will immediately cause the opening modal to close.
 *
 * To address these issues, we create a stack:
 *
 * - `useBackHistory` calls our `register` function.
 * - An ID is created and pushed to our stack.
 * - Our `stackIdx` is incremented, which represents where we are in the stack.
 * - We push our history record.
 * - We initialize a variable `shouldRemoveStackIdOnUnmount`, that tracks whether a history event
 *   occurred that removed or overwrote our pushed history record. If it did, we remove our stack
 *   entry on unmount, BEFORE we calculate our history record cleanup, so we don't go back too far.
 * - Register our history listener
 *
 * The rest of the implementation is described below. The `stack`, `stackIdx`, and
 * `shouldRemoveStackIdOnUnmount` are used to determine how far we `go` back when unmounting. Our
 * `stack` ensures that only the "top" instance handles events.
 */
export const BackHistoryProvider = ({ children }: { children: ReactNode }) => {
	const history = useHistory()
	const stack = useRef<string[]>([])
	const stackIdx = useRef(-1)

	const isTop = (id: string) => {
		const s = stack.current
		return s[s.length - 1] === id
	}

	const register = useFn((onBack: () => void | false) => {
		const id = v4()
		const pathname = history.location.pathname
		history.push(pathname)
		stack.current.push(id)
		stackIdx.current++
		let shouldRemoveStackIdOnUnmount = false

		const unregister = history.listen((location, action) => {
			/**
			 * If this isn't the top of the stack, ignore. Generally, we don't actually "stack" modals,
			 * but this will happen when a root component `Foo` is in charge of displaying modals `A`
			 * and `B`: If `Foo` opens `A` and then a handler in `A` opens `B`, even if the modals are
			 * just being swapped (think `modalState = useState<'A' | 'B' | null>(null)`), then this
			 * condition will be true.
			 *
			 * We designed it specifically this way as described in the documentation above.
			 */
			if (!isTop(id)) return

			/**
			 * `action` is 'PUSH' or 'REPLACE', meaning there's a navigation. If we are navigating to a
			 * new path, call `onBack()`. Additionally, we set `shouldRemoveStackIdOnUnmount` to true if
			 * the action is a 'REPLACE', since it's just overwriting our original push, and we therefore
			 * do not need to worry about popping that recording via `goBack`.
			 */
			if (action != 'POP') {
				if (location.pathname != pathname) {
					shouldRemoveStackIdOnUnmount = action === 'REPLACE'
					onBack()
				}

				return
			}

			// It's a 'POP', so remove from stack when unmounting.
			shouldRemoveStackIdOnUnmount = true

			/**
			 * If `onBack` returns false, reinstate the history record after a tick. Realistically, this
			 * can only be done once, since the browser will override and let the user go back. There may
			 * be a way around this, but it simply doesn't matter for this implementation right now.
			 */
			if (onBack() === false) {
				microtask(() => {
					history.push(pathname)
					shouldRemoveStackIdOnUnmount = false
				})
			}
		})

		return () => {
			unregister()

			/**
			 * The flag was flipped to remove the ID from the stack. What this really means is that
			 * a history event fired that removed ('POP') or overwrote ('REPLACE') our originally
			 * pushed history record, and we no longer need to count it for determining how far we
			 * need to go back. We thus remove it from our stack of IDs, so it isn't counted.
			 */
			if (shouldRemoveStackIdOnUnmount) {
				stack.current = stack.current.filter((sid) => sid !== id)
			}

			/**
			 * Now, we need to run a microtask, which allows any concurrently rendering instance using
			 * this context to push their history record to the stack.
			 *
			 * Once this fires, we determine the real stack index. A stack index of 0 means there is a
			 * single ID left on the stack. If the stack index is not at 0, we simply return. We only
			 * calculate and perform any `go(n)` or any other cleanup when the final item on the stack
			 * is ready to be cleaned up.
			 *
			 * When we ARE at 0, we determine how far we need to go back based on the number of items
			 * left in the stack. Since 'POP' / 'REPLACE' entries have already been removed by the
			 * `shouldRemoveStackIdOnUnmount` variable check, this leaves records that were pushed but
			 * never popped.
			 *
			 * We also reset the stack and index, since this is the last item.
			 *
			 * If we need to go back, we do so by the amount calculated. But first, we determine the
			 * pathname we're on:
			 *
			 * If the component unmounted due to state or a browser back click, the pathname should be
			 * the same as when we mounted (since we just duplicate the path we're on when mounting).
			 * Having a different pathname indicates that we navigated to another route. If this is the
			 * case, we need to perform some annoying cleanup of the history records.
			 *
			 * If we're on a new pathname when the cleanup microtask runs, we still need to scrub our
			 * history records. Without scrubbing our history records, the user, from the new path, will
			 * be able to navigate back to the previous path. However, if they press "back" again, they
			 * will not go back. The back button will not work for them until they pop off all the
			 * history records added by this provider.
			 *
			 * To prevent this, we have to perform some weird maneuvers. We ultimately need to wait for
			 * our `history.go()` operation to finish (which scrubs the correct number of superfluous
			 * records), then do a `history.replace`. This wait cannot be reliably performed using a
			 * microtask, and a `setTimeout` is long enough that we get a flash before the replacement.
			 *
			 * Originally, I tried using `history.listen` to manage this, and although it worked to
			 * properly queue the `replace`, it's accompanied by a weird side effect: When we unsubscribe
			 * from the `history.listen` after replacing, it somehow unsubscribes the main history
			 * listener for the app. Meaning, going back and forth using the browser history does
			 * nothing, even if the paths change properly. I confirmed that the handlers simply don't
			 * fire within the `history` package at all. Perhaps this has to do with using
			 * `history.listen` outside the normal component lifecycle, but I'm not sure.
			 *
			 * Either way, we can address this issue by instead subscribing directly to the history
			 * 'popstate' event. As soon as it fires, we unsubscribe that event, thus only recording
			 * the first event. If that event pathname matches our original pathname, it means we have
			 * successfully gone back, and we do the replacement.
			 *
			 * Ideally, links within a `useBackHistory` component should use `replace` when doing route
			 * navigation, as this will prevent a superfluous forward record when arriving at the new
			 * route. However, both 'replace' and 'push' work, and the superfluous record with 'push'
			 * doesn't negatively affect anything.
			 */
			microtask(() => {
				if (stackIdx.current-- !== 0) return

				const backNum = -1 * stack.current.length
				stack.current = []
				stackIdx.current = -1

				if (backNum) {
					const currentPathname = history.location.pathname

					if (currentPathname !== pathname) {
						const listener = (_: PopStateEvent) => {
							if (history.location.pathname === pathname) {
								history.replace(currentPathname)
							}

							window.removeEventListener('popstate', listener)
						}

						window.addEventListener('popstate', listener)
					}

					history.go(backNum)
				}
			})
		}
	})

	return <BackHistoryContext.Provider value={register}>{children}</BackHistoryContext.Provider>
}
